import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Award, CalendarDays, ChevronLeft, ChevronRight, Users } from "lucide-react";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1785252881030-ff5351abf2f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const INTERVAL = 7000;

const fallbackSlide = {
  heading: "Where civil professionals move the region forward.",
  subheading: "Diploma Civil Engineers Association",
  description: "A trusted professional network for knowledge, opportunity and meaningful impact across Hubli–Dharwad.",
  image: HERO_FALLBACK
};

const splitHeading = text => {
  const words = (text || "").split(" ");
  if (words.length < 4) return [text, ""];
  const cut = Math.ceil(words.length / 2);
  return [words.slice(0, cut).join(" "), words.slice(cut).join(" ")];
};

export const HeroSlider = ({ slides = [], counts = {} }) => {
  const [i, setI] = useState(0);
  const count = slides.length || 1;

  useEffect(() => {
    const t = setInterval(() => setI(s => (s + 1) % count), INTERVAL);
    return () => clearInterval(t);
  }, [count, i]);

  const s = slides[i] || fallbackSlide;
  const [first, second] = splitHeading(s.heading);
  const chips = [
    { icon: Users, value: `${counts.members || 0}+`, label: "Active members" },
    { icon: Award, value: `${counts.committee || 0}`, label: "Committee leaders" },
    { icon: CalendarDays, value: `${counts.events || 0}+`, label: "Events & programs" }
  ];

  return (
    <section className="hero" data-testid="hero-slider">
      <AnimatePresence mode="sync">
        <motion.div
          key={`bg-${s.id || i}`}
          className="hero-bg"
          style={{ backgroundImage: `url(${s.image || HERO_FALLBACK})` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1 }}
        />
      </AnimatePresence>
      <div className="hero-shade" />
      <div className="hero-grid" />
      <div className="hero-content">
        <div className="hero-copy">
          <AnimatePresence mode="wait">
            <motion.div
              key={s.id || i}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="badge"><i />{s.subheading || "Diploma Civil Engineers Association"}</span>
              <h1>{first} <em className="gold-text">{second}</em></h1>
              <p>{s.description}</p>
              <div className="hero-actions">
                <Link to={s.primary_url || "/about"} className="button button-gold" data-testid="hero-primary-button">
                  {s.primary_label || "Explore DCEA"} <ArrowRight size={16} />
                </Link>
                <Link to={s.secondary_url || "/membership"} className="button button-ghost" data-testid="hero-secondary-button">
                  {s.secondary_label || "Become a member"}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="hero-controls">
            <button onClick={() => setI((i - 1 + count) % count)} data-testid="hero-prev-button" aria-label="Previous slide"><ChevronLeft size={18} /></button>
            <span data-testid="hero-slide-indicator">{String(i + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span>
            <div className="hero-progress"><i key={i} /></div>
            <button onClick={() => setI((i + 1) % count)} data-testid="hero-next-button" aria-label="Next slide"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div className="hero-chips" data-testid="hero-stat-chips">
          {chips.map(({ icon: Icon, value, label }, k) => (
            <motion.div
              className="chip"
              key={label}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.5 + k * 0.15 }}
              data-testid={`hero-chip-${k}`}
            >
              <Icon size={28} />
              <div><strong>{value}</strong><span>{label}</span></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
